import {
  CardItem,
  CardProps,
  SupplierCardType,
} from '@/components/v3/atoms/card/card'
import { GalleryList, Promotion } from '@/types/api/listing-view'
import { Supplier, SupplierLocation } from '@/types/Supplier'
import { appendUTM } from '@/utils/appendUTM'
import { isPromotionActiveNow } from '@/utils/is-promotion-active-now'
import { getVenueListingCapacityInfo } from '@/utils/supplierListingMapData'
import { Prisma } from '@prisma/client'

export function mapSupplierToCardProps(
  suppliers?: Supplier[]
): CardItem[] | undefined {
  return suppliers?.map((supplier) => ({
    id: supplier.id,
    title: supplier.listing_name,
    path: supplier.slug,
    date: '',
    category: supplier.business_category.name,
    locations: supplier.locations,
    imageUrl: supplier.feature_images!.url!,
    imageAlt: supplier.feature_images!.description!,
    instagramLink: supplier.instagram_link,
    websiteLink: supplier.website_url,
  }))
}

type MapPrismaSupplierInput = Prisma.supplierGetPayload<{
  include: {
    business_category: true
    locations: {
      include: {
        location: true
      }
    }
  }
}> & {
  // Optional so existing callers (which don't request this relation) still match.
  articles_related_suppliers?: { id: number }[]
}

export const mapPrismaSupplierToCardProps = (
  suppliers: MapPrismaSupplierInput[],
  options?: { now?: Date; type?: SupplierCardType }
): Omit<CardProps, 'type'>[] => {
  const { now, type } = options ?? {}

  return suppliers.map((supplier) => {
    const featureImages = supplier?.feature_images as {
      url?: string
      description?: string
    } | null

    const promotions = (supplier.promotions ?? []) as unknown as Promotion[]
    const hasActiveOffer = now
      ? promotions.some((promotion) => isPromotionActiveNow(promotion, now))
      : false

    const gallery =
      (supplier.gallery_list as unknown as GalleryList[] | null) ?? []
    const galleryImages = gallery
      .map((item) => item?.url)
      .filter((url): url is string => Boolean(url))
      .slice(0, 4)

    const { venueCapacity, venueHire } = getVenueListingCapacityInfo(
      supplier.dependent_question
    )
    const capacity = type === 'Venue' ? venueCapacity : null
    // Lower bound of the "Rates from" value; the card renders it as "from £X".
    // Null (not rendered) when the supplier hasn't set it.
    const price = venueHire.specificValue || venueHire.min || null

    return {
      id: supplier.id,
      header: supplier.listing_name || '',
      category:
        supplier.business_category?.label ||
        supplier.business_category?.name ||
        '',
      imageUrl: featureImages?.url || '',
      imageAlt: featureImages?.description || '',
      itemId: supplier.id,
      locations: supplier.locations as unknown as SupplierLocation[],
      path: supplier?.slug || '',
      instagramLink: supplier.instagram_link || '',
      websiteLink: appendUTM(
        supplier.website_url,
        supplier.custom_tracking_parameters
      ),
      isFeatured: (supplier.articles_related_suppliers?.length ?? 0) > 0,
      hasActiveOffer,
      capacity,
      price,
      galleryImages,
    }
  })
}
